/**
 * AgentMcpView — MCP server browser surface for the `mcp` category in
 * `OperationPickerPanel` (agent-tools mode).
 *
 * Three sources, surfaced behind a three-tab switcher:
 *   - Microsoft  → managed control-plane MCP servers (resolved from connector
 *                  ids whose path marks them as Microsoft-published)
 *   - Others     → BYO MCP servers persisted in connections.json (agentMcpConnections map)
 *   - All        → union of both, plus a leading "Add MCP server" card
 *
 * Selection model: clicking a card adds the entire server as an MCP tool to the
 * agent. The runtime is told `allowedTools` is omitted (= all tools allowed by
 * default). The user narrows the allowed tools later from the node-editor's
 * MCP config tab, which is where `listMcpTools` actually runs.
 *
 * Managed (control-plane) cards add the MCP tool to the agent with the
 * connector + resolved `mcpServerPath` baked in but no connection yet; the
 * node editor's MCP config tab creates the managed-API connection and runs
 * list-tools, mirroring Otto's existing managed-API connection pattern. BYO
 * cards persist the connection key directly to
 * `connections.json.agentMcpConnections` via the BYO add form.
 *
 * Mirrors the LogicAppsUX designer-v2 picker layout
 * (`libs/designer-v2/.../mcp/browse/mcpServersBrowse.tsx`).
 */

import { useCallback, useId, useMemo, useState } from 'react';
import {
  CounterBadge,
  Image,
  Tab,
  TabList,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  PlugDisconnectedRegular,
  ServerRegular,
} from '@fluentui/react-icons';
import {
  useMcpServersQuery,
  useMcpConnectionsForApp,
} from '@/hooks/useMcpServers';
import {
  mcpServerToInfo,
  type McpServerOperation,
} from '@/services/mcpApi';
import {
  ADD_MCP_SERVER_OPERATION_ID,
  builtinMcpServerOperation,
  isAddMcpServerCard,
} from '@/services/mcpHelpers';
import type { McpConnection } from '@/types/logicApps';
import type { MCPServerConfig } from '@/types/mcpTypes';
import { sanitizeErrorMessage } from '@/utils/sanitization';
import { PanelStatus } from './PanelStatus';
import { McpAddServerForm } from './McpAddServerForm';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  tabList: {
    marginBottom: tokens.spacingVerticalXS,
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  partialError: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalS,
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
  },
  serverSection: {
    marginBottom: tokens.spacingVerticalS,
  },
  serverHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    cursor: 'pointer',
    borderRadius: tokens.borderRadiusMedium,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      borderTopColor: tokens.colorNeutralStroke1,
      borderRightColor: tokens.colorNeutralStroke1,
      borderBottomColor: tokens.colorNeutralStroke1,
      borderLeftColor: tokens.colorNeutralStroke1,
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: '2px',
    },
  },
  serverHeaderDisabled: {
    cursor: 'not-allowed',
    opacity: 0.55,
    ':hover': {
      backgroundColor: 'transparent',
      borderTopColor: tokens.colorNeutralStroke2,
      borderRightColor: tokens.colorNeutralStroke2,
      borderBottomColor: tokens.colorNeutralStroke2,
      borderLeftColor: tokens.colorNeutralStroke2,
    },
  },
  addCardHeader: {
    borderTopStyle: 'dashed',
    borderRightStyle: 'dashed',
    borderBottomStyle: 'dashed',
    borderLeftStyle: 'dashed',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  serverIcon: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusSmall,
    objectFit: 'contain',
  },
  serverIconFallback: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase400,
  },
  serverName: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  serverDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginLeft: tokens.spacingHorizontalS,
    maxWidth: '180px',
  },
});

type TabValue = 'all' | 'microsoft' | 'others';

const TAB_LABELS: Record<TabValue, string> = {
  all: 'All',
  microsoft: 'Microsoft',
  others: 'Others',
};

// =============================================================================
// SHARED CARD COMPONENT
// =============================================================================

interface ServerCardProps {
  iconUri?: string | undefined;
  displayName: string;
  description?: string | undefined;
  onClick: () => void;
  disabled?: boolean;
  disabledReason?: string;
  ariaLabel?: string;
}

/**
 * Click-to-add MCP server card. No expansion; the whole card is the click
 * target. When `disabled`, click and keyboard activation are suppressed and
 * the card carries `aria-disabled="true"` so AT users get the same signal.
 */
const ServerCard = ({
  iconUri,
  displayName,
  description,
  onClick,
  disabled = false,
  disabledReason,
  ariaLabel,
}: ServerCardProps) => {
  const styles = useStyles();

  const handleActivate = () => {
    if (disabled) return;
    onClick();
  };

  const card = (
    <div
      className={mergeClasses(
        styles.serverHeader,
        disabled && styles.serverHeaderDisabled,
      )}
      onClick={handleActivate}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      aria-label={ariaLabel ?? `${displayName}, add MCP server`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleActivate();
        }
      }}
    >
      {iconUri ? (
        <Image src={iconUri} alt="" className={styles.serverIcon} />
      ) : (
        <ServerRegular className={styles.serverIconFallback} />
      )}
      <Text className={styles.serverName}>{displayName}</Text>
      {description && (
        <Text className={styles.serverDescription} title={description}>
          {description}
        </Text>
      )}
    </div>
  );

  if (disabled && disabledReason) {
    return (
      <div className={styles.serverSection}>
        <Tooltip content={disabledReason} relationship="description" withArrow>
          {card}
        </Tooltip>
      </div>
    );
  }

  return <div className={styles.serverSection}>{card}</div>;
};

// =============================================================================
// PER-SOURCE ITEM COMPONENTS
// =============================================================================

interface ManagedItemProps {
  server: McpServerOperation;
  onSelect: (server: MCPServerConfig, source: 'managed' | 'byo') => void;
}

/**
 * Managed (control-plane) MCP server card. Clicking adds the MCP tool to the
 * agent with the connector + resolved `mcpServerPath` baked into its
 * parameters but no connection yet — the node editor's MCP config tab handles
 * managed connection creation + list-tools, mirroring Otto's existing
 * managed-API connection pattern.
 */
export const ManagedMcpServerItem = ({ server, onSelect }: ManagedItemProps) => {
  const serverInfo = mcpServerToInfo(server);

  const serverConfig: MCPServerConfig = useMemo(() => {
    const config: MCPServerConfig = {
      id: server.id,
      name: serverInfo.displayName,
      endpoint: server.properties.api?.id ?? '',
      transport: 'http',
    };
    if (serverInfo.description !== undefined) config.description = serverInfo.description;
    if (serverInfo.iconUri !== undefined) config.iconUri = serverInfo.iconUri;
    if (serverInfo.brandColor !== undefined) config.brandColor = serverInfo.brandColor;
    // Project-scoped connector id (e.g. /automationProjects/<proj>/managedApis/<connector>)
    // used to fetch swagger + create the managed connection later.
    if (server.properties.api?.id) config.connectorId = server.properties.api.id;
    // The InvokeMCP operationId, used to resolve the runtime mcpServerPath.
    config.operationId = server.name;
    return config;
  }, [server.id, server.name, server.properties.api?.id, serverInfo]);

  return (
    <ServerCard
      iconUri={serverInfo.iconUri}
      displayName={serverInfo.displayName}
      description={serverInfo.description}
      onClick={() => onSelect(serverConfig, 'managed')}
      ariaLabel={`${serverInfo.displayName} MCP server`}
    />
  );
};

interface ByoItemProps {
  recordKey: string;
  record: McpConnection;
  onSelect: (server: MCPServerConfig, source: 'managed' | 'byo') => void;
}

/**
 * BYO MCP connection card. One per entry in `connections.json.agentMcpConnections`.
 * Clicking attaches the entire server to the agent (all tools allowed by
 * default). Tool listing happens later in the node-editor's MCP config tab.
 */
const ByoMcpConnectionItem = ({ recordKey, record, onSelect }: ByoItemProps) => {
  const displayName = record.displayName?.trim() || recordKey;

  const serverConfig: MCPServerConfig = useMemo(() => {
    const config: MCPServerConfig = {
      id: recordKey,
      name: displayName,
      endpoint: record.mcpServerUrl,
      transport: 'http',
      connectionId: recordKey,
    };
    return config;
  }, [recordKey, displayName, record.mcpServerUrl]);

  return (
    <ServerCard
      displayName={displayName}
      description={record.mcpServerUrl}
      onClick={() => onSelect(serverConfig, 'byo')}
    />
  );
};

interface AddCardProps {
  onClick: () => void;
}

const AddServerCard = ({ onClick }: AddCardProps) => {
  const styles = useStyles();
  return (
    <div className={styles.serverSection}>
      <div
        className={mergeClasses(styles.serverHeader, styles.addCardHeader)}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        // Accessible name starts with the visible label so speech-input
        // users can say "Add MCP server" (WCAG 2.5.3 Label in Name).
        aria-label="Add MCP server — connect to a custom MCP server"
      >
        <AddRegular className={styles.serverIconFallback} aria-hidden="true" />
        <Text className={styles.serverName}>Add MCP server</Text>
        <Text className={styles.serverDescription} aria-hidden="true">
          Connect to a custom MCP server
        </Text>
      </div>
    </div>
  );
};

// =============================================================================
// MAIN VIEW
// =============================================================================

export interface AgentMcpViewProps {
  /** Optional name filter applied to the active tab (matches name + description). */
  searchQuery?: string;
  /**
   * Workflow name forwarded to the BYO add form so its (optional) probe knows
   * which workflow scope to call `listMcpTools` against. Not required for
   * BROWSING — `useMcpConnectionsForApp` is app-scoped — so we treat it as
   * optional and only warn when the user opens the add form without it.
   */
  workflowName: string;
  /**
   * Called when the user picks a server card. `source` lets the caller
   * differentiate BYO vs managed: BYO carries a persisted connection key,
   * while managed carries the connectorId + operationId so the node editor
   * can create the managed connection and resolve `mcpServerPath` later.
   */
  onServerSelect: (server: MCPServerConfig, source: 'managed' | 'byo') => void;
}

export const AgentMcpView = ({
  searchQuery,
  workflowName,
  onServerSelect,
}: AgentMcpViewProps) => {
  const styles = useStyles();
  const serversQuery = useMcpServersQuery();
  const byo = useMcpConnectionsForApp();

  const [selectedTab, setSelectedTab] = useState<TabValue>('all');
  const [addOpen, setAddOpen] = useState(false);

  const handleAddClick = useCallback(() => setAddOpen(true), []);
  const handleAddClose = useCallback(() => setAddOpen(false), []);

  // ---------------------------------------------------------------------
  // Filter helpers — search applies to the active tab only.
  // ---------------------------------------------------------------------
  const q = searchQuery?.trim().toLowerCase() ?? '';
  const matchesManaged = useCallback(
    (server: McpServerOperation) => {
      if (!q) return true;
      const info = mcpServerToInfo(server);
      return (
        info.displayName.toLowerCase().includes(q) ||
        (info.description ?? '').toLowerCase().includes(q)
      );
    },
    [q],
  );
  const matchesByo = useCallback(
    (key: string, record: McpConnection) => {
      if (!q) return true;
      const name = (record.displayName?.trim() || key).toLowerCase();
      return name.includes(q) || record.mcpServerUrl.toLowerCase().includes(q);
    },
    [q],
  );

  // ---------------------------------------------------------------------
  // Per-tab data + counts.
  // ---------------------------------------------------------------------
  const microsoftServers = useMemo(
    () => (serversQuery.data?.shared ?? []).filter(matchesManaged),
    [serversQuery.data?.shared, matchesManaged],
  );
  const otherEntries = useMemo(
    () =>
      Object.entries(byo.records).filter(([key, record]) =>
        matchesByo(key, record),
      ),
    [byo.records, matchesByo],
  );

  // Counts ignore the search query so the user can see how many items are
  // hidden behind a filter. Matches LogicAppsUX behaviour.
  const counts = useMemo(
    () => ({
      microsoft: serversQuery.data?.shared.length ?? 0,
      others: Object.keys(byo.records).length,
    }),
    [serversQuery.data?.shared.length, byo.records],
  );

  const baseTabId = useId();
  const tabId = (value: TabValue) => `${baseTabId}-tab-${value}`;
  const tabPanelId = `${baseTabId}-panel`;

  const tabs: Array<{ value: TabValue; label: string; count: number }> = [
    {
      value: 'all',
      label: TAB_LABELS.all,
      count: counts.microsoft + counts.others,
    },
    { value: 'microsoft', label: TAB_LABELS.microsoft, count: counts.microsoft },
    { value: 'others', label: TAB_LABELS.others, count: counts.others },
  ];

  // ---------------------------------------------------------------------
  // Loading / error reporting — per-source so an empty Microsoft list
  // doesn't hide a usable BYO path.
  // ---------------------------------------------------------------------
  const serversLoading = serversQuery.isLoading;
  const byoLoading = byo.isLoading;
  const serversError = serversQuery.error;
  const byoError = byo.error;

  // ---------------------------------------------------------------------
  // Add-server form area. On success we just switch to the Others tab and
  // close the form; the BYO list re-renders from the invalidated query.
  // ---------------------------------------------------------------------
  const handleAdded = useCallback(() => {
    setSelectedTab('others');
    setAddOpen(false);
  }, []);

  const renderAddForm = () => (
    <McpAddServerForm
      workflowName={workflowName}
      onCancel={handleAddClose}
      onAdded={handleAdded}
    />
  );

  // ---------------------------------------------------------------------
  // Per-tab body rendering.
  // ---------------------------------------------------------------------
  const renderManagedList = (servers: McpServerOperation[], emptyMessage: string) => {
    if (serversLoading) {
      return <PanelStatus variant="loading" message="Loading MCP servers..." />;
    }
    if (serversError && servers.length === 0) {
      return (
        <PanelStatus
          variant="error"
          message={sanitizeErrorMessage(serversError.message) || 'Failed to load MCP servers'}
        />
      );
    }
    if (servers.length === 0) {
      return (
        <PanelStatus
          variant="empty"
          icon={<PlugDisconnectedRegular />}
          message={q ? `No MCP servers match "${searchQuery}"` : emptyMessage}
        />
      );
    }
    return (
      <>
        {servers.map((server) => (
          <ManagedMcpServerItem
            key={server.id}
            server={server}
            onSelect={onServerSelect}
          />
        ))}
      </>
    );
  };

  const renderByoList = (
    entries: Array<[string, McpConnection]>,
    emptyMessage: string,
  ) => {
    if (byoLoading) {
      return <PanelStatus variant="loading" message="Loading MCP connections..." />;
    }
    if (byoError && entries.length === 0) {
      return (
        <PanelStatus
          variant="error"
          message={sanitizeErrorMessage(byoError.message) || 'Failed to load MCP connections'}
        />
      );
    }
    if (entries.length === 0) {
      return (
        <PanelStatus
          variant="empty"
          icon={<PlugDisconnectedRegular />}
          message={q ? `No MCP connections match "${searchQuery}"` : emptyMessage}
        />
      );
    }
    return (
      <>
        {entries.map(([key, record]) => (
          <ByoMcpConnectionItem
            key={key}
            recordKey={key}
            record={record}
            onSelect={onServerSelect}
          />
        ))}
      </>
    );
  };

  const renderAllTab = () => {
    // Combine: BYO entries → Microsoft.
    const totalEmpty =
      microsoftServers.length === 0 &&
      otherEntries.length === 0;

    if (serversLoading && byoLoading && totalEmpty) {
      return <PanelStatus variant="loading" message="Loading MCP servers..." />;
    }

    return (
      <>
        {serversError && byoError && totalEmpty ? (
          <PanelStatus
            variant="error"
            message={
              sanitizeErrorMessage(serversError?.message || byoError?.message || '') ||
              'Failed to load MCP servers'
            }
          />
        ) : (
          <>
            {/* Surface partial failures inline so the user can still use
                whichever source loaded successfully. */}
            {serversError && (
              <div className={styles.partialError} role="status">
                Could not load managed MCP servers ({sanitizeErrorMessage(serversError.message)})
              </div>
            )}
            {byoError && (
              <div className={styles.partialError} role="status">
                Could not load custom MCP servers ({sanitizeErrorMessage(byoError.message)})
              </div>
            )}

            {totalEmpty && !serversLoading && !byoLoading && (
              <PanelStatus
                variant="empty"
                icon={<PlugDisconnectedRegular />}
                message={
                  q
                    ? `No MCP servers match "${searchQuery}"`
                    : 'No MCP servers available'
                }
                {...(!q && { detail: 'Use "Add MCP server" above to register one' })}
              />
            )}

            {otherEntries.map(([key, record]) => (
              <ByoMcpConnectionItem
                key={`all-byo-${key}`}
                recordKey={key}
                record={record}
                onSelect={onServerSelect}
              />
            ))}
            {microsoftServers.map((server) => (
              <ManagedMcpServerItem
                key={`all-ms-${server.id}`}
                server={server}
                onSelect={onServerSelect}
              />
            ))}
          </>
        )}
      </>
    );
  };

  // Body for the currently selected tab. The "Add MCP server" card is pinned
  // at the top of `All` and `Others` (matches LogicAppsUX behaviour).
  const renderBody = () => {
    const showAddCard = selectedTab === 'all' || selectedTab === 'others';
    return (
      <>
        {showAddCard && !addOpen && (
          <AddServerCard onClick={handleAddClick} />
        )}
        {showAddCard && addOpen && renderAddForm()}

        {selectedTab === 'all' && renderAllTab()}
        {selectedTab === 'microsoft' &&
          renderManagedList(microsoftServers, 'No Microsoft MCP servers available')}
        {selectedTab === 'others' &&
          renderByoList(otherEntries, 'No BYO MCP connections yet')}
      </>
    );
  };

  return (
    <div className={styles.root}>
      {addOpen && !workflowName && (
        <PanelStatus
          variant="error"
          message="Save this workflow before adding an MCP server — the connection probe needs a workflow context."
        />
      )}
      <TabList
        className={styles.tabList}
        selectedValue={selectedTab}
        onTabSelect={(_, data) => setSelectedTab(data.value as TabValue)}
        aria-label="MCP server categories"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            id={tabId(tab.value)}
            aria-controls={tabPanelId}
            // Include the count in the accessible name so screen readers
            // announce it. The CounterBadge is aria-hidden because it's a
            // visual presentation of the same info.
            aria-label={`${tab.label}, ${tab.count} ${tab.count === 1 ? 'server' : 'servers'}`}
          >
            <span className={styles.tabLabel}>
              {tab.label}
              <CounterBadge
                count={tab.count}
                showZero
                appearance={selectedTab === tab.value ? 'filled' : 'ghost'}
                color={selectedTab === tab.value ? 'brand' : 'informative'}
                size="small"
                aria-hidden="true"
              />
            </span>
          </Tab>
        ))}
      </TabList>

      <div
        id={tabPanelId}
        role="tabpanel"
        aria-labelledby={tabId(selectedTab)}
      >
        {renderBody()}
      </div>
    </div>
  );
};

// Re-export the synthetic add-card op identifiers for downstream callers
// (e.g. the picker click handler) that need to recognise the + card.
export { ADD_MCP_SERVER_OPERATION_ID, builtinMcpServerOperation, isAddMcpServerCard };
